import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { BlogPost } from '@/lib/types/contentful'
import { getAllBlogPosts } from '@/lib/contentful/api'
import { BlogCard } from './BlogCard'

export function BlogList() {
  const [posts, setPosts] = useState<BlogPost[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const searchQuery = searchParams.get('search')

  useEffect(() => {
    async function fetchPosts() {
      try {
        setLoading(true)
        setError(null)

        const fetchedPosts = await getAllBlogPosts()
        let filteredPosts = fetchedPosts

        if (searchQuery) {
          const query = searchQuery.toLowerCase()
          filteredPosts = fetchedPosts.filter(post => {
            const { title, metaDescription, category } = post.fields
            return (
              title.toLowerCase().includes(query) ||
              (metaDescription && metaDescription.toLowerCase().includes(query)) ||
              (category && category.toLowerCase().includes(query))
            )
          })
        }

        setPosts(filteredPosts)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load blog posts')
      } finally {
        setLoading(false)
      }
    }

    fetchPosts()
  }, [searchQuery])

  if (error) {
    return (
      <div className="text-center py-10">
        <div className="text-red-500 mb-4">{error}</div>
        <p className="text-muted-foreground">Please check your Contentful configuration or try again later.</p>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      <h2 className="text-3xl font-bold mb-10 text-center">
        {searchQuery ? `Search Results: "${searchQuery}"` : 'Latest Articles'}
      </h2>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="animate-pulse bg-card rounded-lg overflow-hidden border border-border/10 h-full">
              <div className="aspect-video bg-muted"></div>
              <div className="p-6">
                <div className="h-4 bg-muted rounded w-1/4 mb-4"></div>
                <div className="h-8 bg-muted rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-muted rounded mb-2"></div>
                <div className="h-4 bg-muted rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {posts.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {posts.map((post) => (
                <BlogCard key={post.sys.id} post={post} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-card rounded-lg border border-border/10">
              <h3 className="text-xl font-medium mb-2">No posts found</h3>
              {searchQuery ? (
                <p className="text-muted-foreground max-w-md mx-auto">
                  No articles matching "{searchQuery}". Try a different search term.
                </p>
              ) : (
                <p className="text-muted-foreground max-w-md mx-auto">
                  Check back soon for new articles!
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}